import { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageSEO from '@/components/base/PageSEO';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, hotDeals, articles } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [slug]);

  const currentCategory = useMemo(
    () => categories.find((c) => c.slug === activeCategory) || categories[0],
    [activeCategory],
  );

  const categoryDeals = useMemo(
    () => hotDeals.filter((d) => d.category === activeCategory),
    [activeCategory],
  );

  const categoryArticles = useMemo(
    () => articles.filter((a) => a.categoryId === activeCategory),
    [activeCategory],
  );

  const schemaJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${currentCategory.name} - 福利铺子`,
    description: currentCategory.description,
    url: `https://fulipuzi.com/category/${activeCategory}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '福利铺子',
      url: 'https://fulipuzi.com',
    },
  };

  return (
    <>
      <PageSEO
        title={`${currentCategory.name} - 福利铺子`}
        description={currentCategory.description}
        keywords={`福利铺子,${currentCategory.name},福利铺子优惠,每日福利折扣,限时秒杀福利,会员专属福利`}
        canonicalUrl={`https://fulipuzi.com/category/${activeCategory}`}
        schemaJsonLd={schemaJsonLd}
      />
      <div className="px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-8">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: currentCategory.name },
          ]}
        />

        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-2">
            {currentCategory.name}
          </h1>
          <p className="text-sm md:text-base text-foreground-600">
            {currentCategory.description}
          </p>
        </div>

        <div className="mb-8">
          <div className="flex items-center gap-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-white text-foreground-600 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        {categoryDeals.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
              <span className="w-1 h-5 bg-primary-500 rounded-full inline-block mr-3 align-middle"></span>
              热门福利
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5" data-product-shop="">
              {categoryDeals.map((deal) => (
                <Link
                  key={deal.id}
                  to={`/article/${deal.id}`}
                  className="group bg-white rounded-xl border border-background-200 hover:border-primary-200 hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer flex flex-col"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={deal.image}
                      alt={deal.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
                        {deal.tag}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-bold rounded whitespace-nowrap">
                        {deal.discount}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {deal.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mb-3 line-clamp-2">
                      {deal.description}
                    </p>
                    <div className="mt-auto flex items-center justify-between">
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-lg font-bold text-primary-600">
                          ¥{deal.currentPrice}
                        </span>
                        {deal.originalPrice > 0 && (
                          <span className="text-xs text-foreground-400 line-through">
                            ¥{deal.originalPrice}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-foreground-400">{deal.expiresIn}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {categoryArticles.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-heading font-bold text-foreground-900 mb-5">
              <span className="w-1 h-5 bg-accent-500 rounded-full inline-block mr-3 align-middle"></span>
              相关攻略
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <article key={article.id}>
                  <Link
                    to={`/article/${article.slug}`}
                    className="group block bg-white rounded-xl border border-background-200 hover:border-primary-200 hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer h-full"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={article.coverImage}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-semibold text-foreground-900 text-base mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 mb-4 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-400">
                        <span className="flex items-center gap-1">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-time-line"></i>
                          </span>
                          {article.readTime}
                        </span>
                        <span>{article.publishedAt}</span>
                      </div>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </section>
        )}

        {categoryDeals.length === 0 && categoryArticles.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-background-100 rounded-full">
              <i className="ri-inbox-line text-3xl text-foreground-400"></i>
            </div>
            <p className="text-foreground-500">该分类暂无内容，敬请期待！</p>
          </div>
        )}
      </div>
    </>
  );
}